import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { SessionAction } from "@/types/admin";

export const ADMIN_SECRET_KEY = "healnova-admin-secret";

/** Session storage only — never localStorage. */
export function readStoredSecret(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(ADMIN_SECRET_KEY);
  } catch {
    return null;
  }
}

export function clearStoredSecret() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ADMIN_SECRET_KEY);
  } catch {
    /* ignore */
  }
}

interface AdminAuthValue {
  secret: string | null;
  isAuthenticated: boolean;
  activity: SessionAction[];
  signIn: (secret: string) => void;
  signOut: () => void;
  recordAction: (action: SessionAction) => void;
}

const AdminAuthContext = createContext<AdminAuthValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [secret, setSecret] = useState<string | null>(() => readStoredSecret());
  const [activity, setActivity] = useState<SessionAction[]>([]);

  const signIn = useCallback((next: string) => {
    try {
      window.sessionStorage.setItem(ADMIN_SECRET_KEY, next);
    } catch {
      /* ignore */
    }
    setSecret(next);
  }, []);

  const signOut = useCallback(() => {
    clearStoredSecret();
    setSecret(null);
    setActivity([]);
  }, []);

  const recordAction = useCallback((action: SessionAction) => {
    setActivity((current) => [action, ...current].slice(0, 25));
  }, []);

  const value = useMemo<AdminAuthValue>(
    () => ({
      secret,
      isAuthenticated: Boolean(secret),
      activity,
      signIn,
      signOut,
      recordAction,
    }),
    [secret, activity, signIn, signOut, recordAction],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used inside <AdminAuthProvider>");
  return ctx;
}
